import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactTooltip from 'react-tooltip';
import styles from './ReminderTriggerForm.module.scss';
import { useFieldArray, UseFormMethods } from 'react-hook-form';
import { ReminderTriggerViewModel } from 'api/reminders/models/ReminderViewModel';
import { ReactComponent as TrashIcon } from 'assets/svg/trash_no_color.svg';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import ReminderTriggerFormItem from '../reminderTriggerFormItem/ReminderTriggerFormItem';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import { FaBell, FaChevronDown, FaPlus } from 'react-icons/fa';

type Props = {
    form: UseFormMethods<any>;
    isDetails: boolean;
    criteriaOptions: SelectValueLabel[];
    measureOptions: SelectValueLabel[];
    responsablesOptions: SelectValueLabel[];
    designation?: string;
    control: number;
    onCreateResponsableOption: (value: any) => void;
    defaultFirstDate?: Date;
    defaultFirstKm?: number;
    defaultFirstHour?: number;
    title?: string;
};

const ReminderTriggerForm: React.FC<Props> = ({ form, isDetails, control, designation, criteriaOptions, measureOptions, responsablesOptions, onCreateResponsableOption, defaultFirstDate, defaultFirstKm, defaultFirstHour, title }: Props) => {
    const { t } = useTranslation();

    const triggers = useFieldArray<ReminderTriggerViewModel>({ control: form.control, name: 'triggers', keyName: 'formId' as any });
    const [showRemoveTrigger, setShowRemoveTrigger] = useState<boolean>(false);
    const [showRemoveAllTriggers, setShowRemoveAllTriggers] = useState<boolean>(false);
    const [triggerToRemoveIndex, setTriggerToRemoveIndex] = useState<number | null>(null);
    const [showTriggers, setShowTriggers] = useState<boolean>(isDetails);
    const [collapseAllCounter, setCollapseAllCounter] = useState<number>(0);

    const controlTriggerCount = triggers.fields.filter(x => x.control === control).length;
    const tooltipId = `reminder-tooltip-${control}`;

    const addTrigger = () => {
        // Save current trigger values before append (RHF v6 useFieldArray overwrites existing values on append)
        const currentTriggerValues = form.getValues('triggers') || [];

        // Pre-filled criteria priority: km, then usage hours, then date
        const useKms = defaultFirstKm && criteriaOptions.some(o => o.value === 'KMS');
        // Hours triggers are whole numbers in the backend; the form fields can hold decimals (e.g. 600,50 h)
        const firstHour = Number(defaultFirstHour) > 0 ? Math.round(Number(defaultFirstHour)) : undefined;
        const useHours = !useKms && firstHour && criteriaOptions.some(o => o.value === 'HOURS');
        triggers.append({
            criteria: useKms ? 'KMS' : useHours ? 'HOURS' : 'DATE',
            active: true,
            beforeDateMeasure: 'DAY',
            repeatDateMeasure: 'DAY',
            designation: designation,
            control: control,
            firstDate: defaultFirstDate ? new Date(defaultFirstDate) : undefined,
            firstKm: useKms ? defaultFirstKm : undefined,
            firstHour: useHours ? firstHour : undefined,
        });

        // Restore existing trigger values that useFieldArray may have overwritten
        setTimeout(() => {
            currentTriggerValues.forEach((triggerVal: any, idx: number) => {
                if (triggerVal) {
                    Object.keys(triggerVal).forEach(key => {
                        if (key !== 'formId') {
                            form.setValue(`triggers[${idx}].${key}`, triggerVal[key]);
                        }
                    });
                }
            });
        }, 0);

        setCollapseAllCounter(c => c + 1);
        setShowTriggers(true);
    }

    const removeItem = () => {
        if (triggerToRemoveIndex === null) return;
        // Save current trigger values before remove (RHF v6 useFieldArray workaround)
        const currentTriggerValues = form.getValues('triggers') || [];
        triggers.remove(triggerToRemoveIndex);
        // Restore remaining trigger values
        setTimeout(() => {
            const remaining = currentTriggerValues.filter((_: any, idx: number) => idx !== triggerToRemoveIndex);
            remaining.forEach((triggerVal: any, idx: number) => {
                if (triggerVal) {
                    Object.keys(triggerVal).forEach(key => {
                        if (key !== 'formId') {
                            form.setValue(`triggers[${idx}].${key}`, triggerVal[key]);
                        }
                    });
                }
            });
        }, 0);
        setShowRemoveTrigger(false);
    }

    const removeAllItems = () => {
        const indicesToRemove = triggers.fields
            .map((field, idx) => ({ field, idx }))
            .filter(({ field }) => field.control === control)
            .map(({ idx }) => idx)
            .reverse();
        // Save current trigger values before remove (RHF v6 useFieldArray workaround)
        const currentTriggerValues = form.getValues('triggers') || [];
        indicesToRemove.forEach(idx => triggers.remove(idx));
        // Restore remaining trigger values
        setTimeout(() => {
            const keepIndices = new Set(indicesToRemove);
            const remaining = currentTriggerValues.filter((_: any, idx: number) => !keepIndices.has(idx));
            remaining.forEach((triggerVal: any, idx: number) => {
                if (triggerVal) {
                    Object.keys(triggerVal).forEach(key => {
                        if (key !== 'formId') {
                            form.setValue(`triggers[${idx}].${key}`, triggerVal[key]);
                        }
                    });
                }
            });
        }, 0);
        setShowRemoveAllTriggers(false);
    }

    const setRemoveItem = (index: number) => {
        setShowRemoveTrigger(true);
        setTriggerToRemoveIndex(index);
    }

    const canToggle = !(isDetails && controlTriggerCount === 0);

    let displayIndex = 0;

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div
                    className={styles.headerToggle}
                    onClick={() => { if (canToggle) setShowTriggers(!showTriggers); }}
                    style={{ cursor: canToggle ? 'pointer' : 'default' }}
                >
                    <FaBell className={styles.headerBellIcon} />
                    <span
                        className={styles.headerTitle}
                        data-tip={controlTriggerCount === 0 ? t('reminders.notifications.empty_hint') as string : undefined}
                        data-for={controlTriggerCount === 0 ? tooltipId : undefined}
                    >
                        {controlTriggerCount} {title || t('reminders.notifications.title')}
                    </span>
                    <span className={styles.headerLine} />
                </div>
                {!isDetails && (
                    <div className={styles.headerActions}>
                        <button
                            type="button"
                            className={styles.headerActionAdd}
                            onClick={addTrigger}
                        >
                            <FaPlus />
                            <span>{t('reminders.notifications.add_trigger_button')}</span>
                        </button>
                        {controlTriggerCount > 1 && (
                            <button
                                type="button"
                                className={styles.headerActionDelete}
                                onClick={() => setShowRemoveAllTriggers(true)}
                            >
                                <TrashIcon />
                                <span>{t('reminders.notifications.delete_all')}</span>
                            </button>
                        )}
                    </div>
                )}
                {controlTriggerCount > 0 && (
                    <FaChevronDown
                        className={`${styles.headerChevron} ${showTriggers ? styles.headerChevronOpen : ''}`}
                        onClick={() => { if (canToggle) setShowTriggers(!showTriggers); }}
                        style={{ cursor: canToggle ? 'pointer' : 'default' }}
                    />
                )}
            </div>

            {showTriggers && controlTriggerCount > 0 && (
                <div className={styles.content}>
                    {triggers.fields.map((ti, index) => {
                        if (ti.control !== control) return null;
                        displayIndex++;
                        const isLastOfControl = displayIndex === controlTriggerCount;
                        return (
                            <ReminderTriggerFormItem
                                key={ti.formId}
                                form={form}
                                criteriaOptions={criteriaOptions}
                                measureOptions={measureOptions}
                                responsablesOptions={responsablesOptions}
                                isDetails={isDetails}
                                onRemoveClick={() => {
                                    setRemoveItem(index);
                                }}
                                index={index}
                                displayIndex={displayIndex}
                                triggersCounter={triggers.fields.length}
                                onCreateResponsableOption={onCreateResponsableOption}
                                collapseAllCounter={collapseAllCounter}
                                isLastOfControl={isLastOfControl}
                            />
                        );
                    })}
                </div>
            )}

            <QuestionYesNo
                message={t('common.messages.remove_record')}
                isVisible={showRemoveTrigger}
                onYes={() => removeItem()}
                onNo={() => { setShowRemoveTrigger(false); setTriggerToRemoveIndex(null) }}
            />
            <QuestionYesNo
                message={t('common.messages.remove_record')}
                isVisible={showRemoveAllTriggers}
                onYes={() => removeAllItems()}
                onNo={() => setShowRemoveAllTriggers(false)}
            />
            <ReactTooltip id={tooltipId} place="top" type="dark" effect="solid" />
        </div>
    );
}

export default ReminderTriggerForm;
